import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet'; 

const HomePage = () => {
  const [selectedLanguage, setSelectedLanguage] = useState('python');
  const [displayedCode, setDisplayedCode] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  const codeExamples = {
    python: `# Python code to send SMS using our API
import requests

url = "https://smstextr.web.app/api/sendsms"
payload = {"to": "+1 123-456-7890", 
            "message": "Hello from SMS Textr API!",
            "apikey": "Your_API_Key" }

response = requests.post(url, json=payload)
print(response.json())`,
    curl: `REM Make sure this is formatted as a single line with no line breaks
          curl -X POST "https://smstextr.web.app/api/sendsms"
               --data-urlencode "to=+1 123-456-7890"
               --data-urlencode "message=Hello from SMS Textr API!"
               --data-urlencode "apikey=Your_API_Key"`,
    ruby: `# Ruby code to send SMS using our API
require 'net/http'
require 'uri'

uri = URI.parse("https://smstextr.web.app/api/sendsms")
response = Net::HTTP.post_form(uri, {
               "to" => "+1 123-456-7890",
               "message" => "Hello from SMS Textr API!",
               "apikey" => "Your_API_Key"
            })
puts response.body`,
    nodejs: `// Node.js code to send SMS using our API
const axios = require('axios');

axios.post('https://smstextr.web.app/api/sendsms', {
            to: '+1 123-456-7890',
            message: 'Hello from SMS Textr API!',
            apikey: 'Your_API_Key'
})
.then(response => console.log(response.data))
.catch(error => console.error('Error:', error.response.data));`,
    javascript: `// JavaScript code to send SMS using our API
fetch('https://smstextr.web.app/api/sendsms', {
  method: 'POST',
  headers: {'Content-Type': 'application/json'},
  body: JSON.stringify({
    to: '+1 123-456-7890',
    message: 'Hello from SMS Textr API!',
    apikey: 'Your_API_Key'
  }),
})
.then(response => response.json()) 
.then(data => console.log(data));`
  };

  useEffect(() => {
    setCurrentIndex(0);
    setDisplayedCode('');
  }, [selectedLanguage]);

  useEffect(() => {
    
    if (currentIndex < codeExamples[selectedLanguage].length) {
      const timer = setTimeout(() => {
        setDisplayedCode(prevCode => prevCode + codeExamples[selectedLanguage][currentIndex]);
        setCurrentIndex(prevIndex => prevIndex + 1);
      }, 20); // Adjust typing speed here

      return () => clearTimeout(timer);
    }
  }, [currentIndex, selectedLanguage]);

  return (
    <div style={{ backgroundColor: '#f0f0f0', padding: '1rem', marginTop: '0' }}>
      {/* Add Helmet to dynamically set title and meta description */}
      <Helmet>
        <title>SMS Textr | Send and Receive SMS with Our Easy API</title> {/* Set the page title */}
        <meta name="description" content="Send SMS with SMS Textr API. Easy integration and no subscription required. Try SMS Textr API to send SMS messages with just a few lines of code." />
        <meta name="keywords" content="SMS, API, send SMS, SMS Textr, messaging, API integration" />
        <meta name="author" content="SMS Textr Team" />
      </Helmet>
      
      <h1 style={{ 
        fontWeight: 'bold', 
        fontSize: '2.2rem', 
        color: '#333333', 
        textAlign: 'center', 
        margin: '1rem 0 2rem',
        textShadow: '1px 1px 2px rgba(0, 0, 0, 0.2)'
      }}>
        Send and Receive SMS with our simple API
      </h1>
      <div style={{ 
        display: 'flex',
        maxWidth: '800px',
        margin: '-1rem auto 0',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          backgroundColor: '#f0f0f0',
          padding: '10px',
          borderRadius: '8px 0 0 8px',
          width: '200px'
        }}>
          {Object.keys(codeExamples).map((lang) => (
            <div
              key={lang}
              onClick={() => setSelectedLanguage(lang)}
              style={{
                cursor: 'pointer',
                padding: '10px',
                backgroundColor: selectedLanguage === lang ? '#d0d0d0' : 'transparent',
                borderRadius: '4px',
                marginBottom: '5px',
                color: '#333'
              }}
            >
              {lang.charAt(0).toUpperCase() + lang.slice(1)}
            </div>
          ))}
        </div>
        <div style={{ 
          backgroundColor: '#1e1e1e', 
          borderRadius: '0 8px 8px 0',
          padding: '15px',
          flexGrow: 1,
          overflowX: 'auto'
        }}>
          <pre style={{ 
            color: '#d4d4d4', 
            fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
            fontSize: '14px',
            lineHeight: '1.5',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word'
          }}>
            <code>
              {displayedCode}
            </code>
          </pre>
        </div>
      </div>
      <div style={{ textAlign: 'center', marginTop: '0.5rem', display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
        <Link to="/login" style={{
          backgroundColor: '#28a745',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '5px',
          textDecoration: 'none',
          fontWeight: 'bold',
          margin: '10px'
        }}>
          Login to Buy Credits
        </Link>
        <Link to="/docs" style={{
          backgroundColor: '#28a745',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '5px',
          textDecoration: 'none',
          fontWeight: 'bold',
          margin: '10px'
        }}>
          View Full Documentation
        </Link>
      </div>
      <p style={{ 
        fontWeight: 'bold', 
        fontSize: '1rem', 
        color: '#333333', 
        textAlign: 'center', 
        margin: '1rem 0 2rem'
      }}>
        You can now send and receive SMS using our API to the following countries:<br/>
        <strong>USA, Canada, India, Germany, UK, Thailand, Australia, Spain, Portugal, Italy, Denmark, Ireland, Luxembourg </strong> and <strong>Poland</strong><br/>
        All new users get 3 free credits to try out our API prior to purchasing more credits.
      </p>
      <div style={{ 
        position: 'static',
        bottom: '0', 
        left: '0', 
        width: '100%',
        fontSize: '12px', 
        backgroundColor: '#f0f0f0',
        padding: '10px 10px',
        textAlign: 'left'
      }}>
        <a href="/privacy" style={{ color: '#007BFF' }}>
          Privacy Policy
        </a>
        {' | '}
        <a href="/tos" style={{ color: '#007BFF' }}>
          Terms of Service
        </a>
      </div>
      </div>
  );
};

export default HomePage;
